const QUOTE_MARKERS = [
  /^On\s+.+wrote:/i,
  /^-{2,}\s*Original Message\s*-{2,}$/i,
  /^_{8,}$/i,
  /^From:\s.+/i
];

const DISCLAIMER_MARKERS = [
  /the contents of this e-?mail and its attachments are intended solely/i,
  /this e-?mail transmission may be confidential/i,
  /confidentiality notice/i,
  /this message and any attachments are confidential/i
];

function decodeEntities(value) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function repairFlattenedEmailText(value) {
  let repaired = value;
  const lines = repaired.split('\n');
  const hasFlattenedLine = lines.some(line => line.length > 220);
  if (!hasFlattenedLine && lines.length > 2) return repaired;

  repaired = repaired
    .replace(/^(Hi|Hello|Hey|Dear)(\s+[^,\n]{1,60}),(?=\S)/i, '$1$2,\n')
    .replace(/(Details below:)(?=\S)/gi, '$1\n')
    .replace(/(https?:\/\/[^\s<>"']+?\.pdf)(?=[A-Z][a-z])/gi, '$1\n')
    .replace(/([.!?])(?=(?:Thanks|Thank you|Best regards|Regards|Sincerely),)/g, '$1\n')
    .replace(/(\b(?:Thanks|Thank you|Best regards|Regards|Sincerely),)(?=[A-Z][a-z]+)/g, '$1\n')
    .replace(/([A-Za-z])(?=On\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s+.+wrote:)/i, '$1\n');

  [
    'Available SF:',
    'Asking rate:',
    'Asking rent:',
    'Ops Ex / NNN:',
    'Clear height:',
    'Dock doors:',
    'Drive-in doors:',
    'Power:',
    'Tour availability:',
    'Question for your client:',
    'Flyer / floor plan PDF:',
    'Flyer/floor plan:',
    'Listing broker comments:',
    'Floor plan PDF:'
  ].forEach(label => {
    repaired = repaired.replace(
      new RegExp(`([^\\n])(${escapeRegExp(label)})`, 'gi'),
      '$1\n$2'
    );
  });

  return repaired;
}

function lineLooksLikeQuoteHeader(lines, index) {
  const stripped = lines[index].trim();
  if (!QUOTE_MARKERS.some(pattern => pattern.test(stripped))) return false;

  if (!/^From:\s.+/i.test(stripped)) return true;

  const nextFew = lines.slice(index + 1, index + 5).map(line => line.trim());
  return nextFew.some(line => /^(Sent|To|Subject|Cc):\s*/i.test(line));
}

function earliestMarkerIndex(text, patterns) {
  return patterns.reduce((earliest, pattern) => {
    const match = text.match(pattern);
    if (!match || match.index === undefined) return earliest;
    return earliest === -1 ? match.index : Math.min(earliest, match.index);
  }, -1);
}

export function trimEmailDisplayText(value) {
  if (!value) return '';

  const decoded = decodeEntities(String(value))
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u00a0/g, ' ');

  const lines = repairFlattenedEmailText(decoded).split('\n');
  const kept = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const stripped = line.trim();
    const disclaimerIndex = earliestMarkerIndex(line, DISCLAIMER_MARKERS);

    if (disclaimerIndex >= 0) {
      const usefulPrefix = line.slice(0, disclaimerIndex).trim();
      if (usefulPrefix) kept.push(usefulPrefix);
      break;
    }

    if (lineLooksLikeQuoteHeader(lines, index)) break;
    if (stripped === '>' || stripped.startsWith('> ')) continue;

    kept.push(line);
  }

  return kept
    .join('\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function sanitizeDom(html) {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  temp.querySelectorAll('script, style, iframe, object, embed, form').forEach(el => el.remove());
  temp.querySelectorAll('.gmail_quote, .gmail_extra, .yahoo_quoted, blockquote').forEach(el => el.remove());

  temp.querySelectorAll('img').forEach(el => {
    const src = el.getAttribute('src') || '';
    if (src.startsWith('cid:')) el.remove();
  });

  temp.querySelectorAll('*').forEach(el => {
    Array.from(el.attributes).forEach(attr => {
      if (attr.name.startsWith('on')) el.removeAttribute(attr.name);
    });
  });

  temp.querySelectorAll('a').forEach(el => {
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer');
  });

  return temp;
}

function normalizeForCompare(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function textFromBody(body) {
  if (!body) return '';
  const rawContent = typeof body === 'object' ? body.content : body;
  if (!rawContent) return '';

  const content = String(rawContent).replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*>/gi, '');
  if (/<[a-z][\s\S]*>/i.test(content)) {
    return sanitizeDom(content).textContent || '';
  }
  return decodeEntities(content);
}

export function sanitizeMessageHtml(body) {
  if (!body) return '';

  const rawContent = typeof body === 'object' ? body.content : body;
  if (!rawContent) return '';

  let content = String(rawContent).replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*>/gi, '');
  const hasHtml = /<[a-z][\s\S]*>/i.test(content);

  if (hasHtml) {
    const temp = sanitizeDom(content);
    const originalText = temp.textContent || '';
    const trimmedText = trimEmailDisplayText(originalText);

    if (trimmedText && normalizeForCompare(trimmedText) !== normalizeForCompare(originalText)) {
      return escapeHtml(trimmedText).replace(/\n/g, '<br>');
    }

    return temp.innerHTML;
  }

  const trimmedText = trimEmailDisplayText(content);
  return escapeHtml(trimmedText).replace(/\n/g, '<br>');
}

export function getMessageDisplayParts(body) {
  const rawText = textFromBody(body)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u00a0/g, ' ')
    .trim();
  const displayText = trimEmailDisplayText(rawText);

  return {
    html: sanitizeMessageHtml(body),
    rawText,
    wasTrimmed: Boolean(displayText) && normalizeForCompare(displayText) !== normalizeForCompare(rawText)
  };
}
